/**
 * Render a `TripDocumentData` (see `./tripDocument.js`) as a fully self-
 * contained HTML string suitable for printing. Every style is inline / in a
 * `<style>` tag so the document displays identically whether it's opened
 * inside a same-origin popup, downloaded as HTML, or piped through a
 * browser's built-in "Save as PDF" flow.
 *
 * Design principles
 *  - Single sans-serif family across the whole document (no accidental
 *    serif accents — every heading and body text uses the same stack, just
 *    at different weights + tracking).
 *  - Letter-size, targeted at ONE page. Padding, font sizes, and line
 *    heights are tuned so a typical trip (round-trip, 1–2 passengers,
 *    ≤12 payments) fits without a page break. Longer schedules flow onto a
 *    second page without breaking a section in half.
 *  - No text wraps that create dead space. Trip meta / cost breakdown
 *    labels + values are laid out with grid so labels don't over-consume
 *    horizontal room, and section titles are `white-space: nowrap` at
 *    small sizes.
 *  - Defera "royal blue" (#0F1952) as the sole accent color.
 */

import {
  formatLongDate,
  formatShortDate,
  formatTime12,
  formatMoney,
  formatMoneyCents,
} from './tripDocument.js';

const BRAND_COLOR = '#0F1952';
const BRAND_SOFT = '#f4f6fb';
const BRAND_TEXT_MUTED = '#5b6478';
const BRAND_RULE = '#dbe0eb';

const SANS_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Helvetica Neue", sans-serif';

function escapeHtml(input) {
  return String(input == null ? '' : input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const esc = escapeHtml;

function renderKV(label, value) {
  if (!value && value !== 0) return '';
  return `
    <div class="kv-row">
      <span class="kv-label">${esc(label)}</span>
      <span class="kv-value">${esc(value)}</span>
    </div>
  `;
}

function renderPassenger(p, idx) {
  const meta = [];
  if (p.dateOfBirth) meta.push(`DOB ${esc(formatShortDate(p.dateOfBirth))}`);
  if (p.gender) meta.push(esc(p.gender.charAt(0).toUpperCase() + p.gender.slice(1)));
  if (p.type && p.type !== 'adult') meta.push(esc(p.type.charAt(0).toUpperCase() + p.type.slice(1)));
  if (p.nationality) meta.push(`Nat. ${esc(p.nationality)}`);

  const contactBits = [];
  if (p.email) contactBits.push(esc(p.email));
  if (p.phoneNumber) contactBits.push(esc(p.phoneNumber));

  const travelBits = [];
  if (p.knownTravelerNumber) travelBits.push(`KTN ${esc(p.knownTravelerNumber)}`);
  if (p.frequentFlyerProgram || p.frequentFlyerNumber) {
    const ff = [p.frequentFlyerProgram, p.frequentFlyerNumber].filter(Boolean).join(' ');
    travelBits.push(`FF ${esc(ff)}`);
  }
  if (p.seatPreference) travelBits.push(`Seat: ${esc(p.seatPreference)}`);

  // Combine contact + travel bits onto a single trailing line when both are
  // short — halves passenger card height for the common case.
  const trailer = [...contactBits, ...travelBits].filter(Boolean).join(' · ');

  return `
    <div class="pax-row">
      <div class="pax-number">${idx + 1}</div>
      <div class="pax-body">
        <div class="pax-name">${esc(p.fullName || 'Passenger')}${meta.length ? `<span class="pax-meta"> · ${meta.join(' · ')}</span>` : ''}</div>
        ${trailer ? `<div class="pax-line">${trailer}</div>` : ''}
      </div>
    </div>
  `;
}

function renderSegments(segments) {
  if (!segments.length) return '';
  const groups = [];
  let currentSlice = -1;
  let currentGroup = null;
  segments.forEach((seg) => {
    if (seg.sliceIndex !== currentSlice) {
      currentGroup = { sliceIndex: seg.sliceIndex, legs: [] };
      groups.push(currentGroup);
      currentSlice = seg.sliceIndex;
    }
    currentGroup.legs.push(seg);
  });

  return groups.map((group, gIdx) => {
    const firstLeg = group.legs[0];
    const lastLeg = group.legs[group.legs.length - 1];
    const heading = `${firstLeg.origin} → ${lastLeg.destination}`;
    const dateHead = formatLongDate(firstLeg.departureDate);
    const groupLabel = groups.length > 1
      ? (gIdx === 0 ? 'Outbound' : gIdx === 1 ? 'Return' : `Leg ${gIdx + 1}`)
      : 'Itinerary';
    const legs = group.legs.map((leg) => {
      const flightNo = leg.marketingCarrierCode && leg.flightNumber
        ? `${leg.marketingCarrierCode}${leg.flightNumber}`
        : (leg.flightNumber || '');
      const metaBits = [
        leg.marketingCarrier || '',
        flightNo,
        leg.aircraft,
        leg.cabinClass,
      ].filter(Boolean).map(esc);
      // Per-leg dates are intentionally omitted here — the slice header
      // above (segment-date) already prints the full departure date once,
      // and repeating it on every leg row is the main source of wasted
      // horizontal room that pushes the airline/flight/aircraft/cabin meta
      // column into ellipsis truncation. Overnight legs still show clearly
      // because we print the arrival date in the slice header (which uses
      // the last leg's arrival day).
      //
      // Airline tail logo is loaded from the same Duffel-hosted URL the
      // Trips page uses; the button component pre-fetches it and rewrites
      // .marketingCarrierLogoUrl to a data URI so the print engine has
      // the bytes locally. Rows without a URL still reserve the column so
      // segments stay vertically aligned.
      const logoSrc = leg.marketingCarrierLogoUrl || '';
      const logoCell = logoSrc
        ? `<img src="${esc(logoSrc)}" alt="${esc(leg.marketingCarrier || '')}" />`
        : '<span class="seg-logo-placeholder" aria-hidden="true"></span>';
      // Layout: primary row = airline logo · depart time+code · → · arrive
      // time+code. All metadata (carrier · flight # · aircraft · cabin)
      // lives on a second line INSIDE the same card, so aircraft names like
      // "Boeing 737 MAX 8 Passenger" and cabin names like "Main Plus" don't
      // fight for horizontal room with the times/codes. `white-space: normal`
      // on .seg-meta lets it wrap to a third line for extreme cases
      // (Delta 767-400ER Boeing on a long international itinerary), rather
      // than ellipsis-truncating and losing the cabin — losing the cabin
      // is what the customer notices first.
      return `
        <div class="seg-row">
          <div class="seg-primary">
            <div class="seg-logo">${logoCell}</div>
            <div class="seg-side seg-side-left">
              <span class="seg-time">${esc(formatTime12(leg.departingAt) || '—')}</span>
              <span class="seg-code">${esc(leg.origin || '—')}</span>
            </div>
            <div class="seg-arrow">→</div>
            <div class="seg-side seg-side-right">
              <span class="seg-time">${esc(formatTime12(leg.arrivingAt) || '—')}</span>
              <span class="seg-code">${esc(leg.destination || '—')}</span>
            </div>
          </div>
          ${metaBits.length ? `<div class="seg-meta">${metaBits.join(' · ')}</div>` : ''}
        </div>
      `;
    }).join('');

    return `
      <div class="segment-group">
        <div class="segment-head">
          <span class="segment-title">${esc(groupLabel)} · ${esc(heading)}</span>
          <span class="segment-date">${esc(dateHead)}</span>
        </div>
        ${legs}
      </div>
    `;
  }).join('');
}

function renderPaymentTimeline(payment) {
  const { paidRows, upcomingRows, pastDueRows, currency, totalPaidCents, remainingCents, totalCents } = payment;

  const rows = [
    ...paidRows.map((r) => ({ ...r, hint: 'Paid' })),
    ...pastDueRows.map((r) => ({ ...r, hint: 'Past due' })),
    ...upcomingRows.map((r) => ({ ...r, hint: 'Upcoming' })),
  ];

  if (!rows.length) {
    return `<div class="pay-empty">No payment schedule on file.</div>`;
  }

  const listItems = rows.map((r) => {
    const dateStr = formatLongDate(r.date) || '—';
    const amountStr = r.amountCents === 0
      ? '—'
      : formatMoneyCents(r.amountCents, r.currency || currency);
    const cls = r.status === 'paid' || r.status === 'paid_ahead'
      ? 'paid'
      : (r.status === 'past_due' ? 'pastdue' : (r.status === 'skipped' ? 'skipped' : 'upcoming'));
    return `
      <tr class="pay-row pay-row-${cls}">
        <td class="pay-status">
          <span class="pay-dot pay-dot-${cls}"></span>
          <span class="pay-hint">${esc(r.hint)}</span>
        </td>
        <td class="pay-date">${esc(dateStr)}</td>
        <td class="pay-note">${esc(r.label || '')}</td>
        <td class="pay-amount">${esc(amountStr)}</td>
      </tr>
    `;
  }).join('');

  const paidTxt = formatMoneyCents(totalPaidCents, currency);
  const remainingTxt = formatMoneyCents(remainingCents, currency);
  const totalTxt = formatMoneyCents(totalCents, currency);

  return `
    <table class="pay-table">
      <thead>
        <tr>
          <th class="pay-status">Status</th>
          <th class="pay-date">Date</th>
          <th class="pay-note">Description</th>
          <th class="pay-amount">Amount</th>
        </tr>
      </thead>
      <tbody>${listItems}</tbody>
      <tfoot>
        <tr class="pay-total-row">
          <td colspan="3">Paid to date</td>
          <td class="pay-amount">${esc(paidTxt)}</td>
        </tr>
        <tr class="pay-total-row">
          <td colspan="3">Balance remaining</td>
          <td class="pay-amount">${esc(remainingTxt)}</td>
        </tr>
        <tr class="pay-total-row pay-total-final">
          <td colspan="3">Trip total</td>
          <td class="pay-amount">${esc(totalTxt)}</td>
        </tr>
      </tfoot>
    </table>
  `;
}

function renderCostBreakdown(pricing) {
  // Fee-percentage annotations (e.g. "Service fee (12%)") are intentionally
  // omitted here: the underlying rates change from booking to booking
  // (service-fee tier flips at $300 base fare, Slice vs Capital, refundable
  // fares get 0% down) and the on-screen breakdown already surfaces them
  // with all the disclaimer copy. The statement is a summary — showing just
  // the dollar amounts keeps the row set focused on what the customer owes.
  const rows = [
    ['Base fare', formatMoney(pricing.baseFare, pricing.currency)],
  ];
  // Seats picked at checkout are financed with the fare, so they belong above the
  // fees that are charged on top of them — not hidden inside "Base fare".
  if (pricing.seatTotal > 0) {
    const seatLabel = pricing.seatCount > 1 ? `Seat selection (${pricing.seatCount})` : 'Seat selection';
    rows.push([seatLabel, formatMoney(pricing.seatTotal, pricing.currency)]);
  }
  if (pricing.creditsTotal > 0) {
    rows.push(['Credits applied', `− ${formatMoney(pricing.creditsTotal, pricing.currency)}`]);
  }
  if (pricing.serviceFee > 0) {
    rows.push(['Service fee', formatMoney(pricing.serviceFee, pricing.currency)]);
  }
  if (pricing.holdFee > 0) {
    rows.push(['Hold fee', formatMoney(pricing.holdFee, pricing.currency)]);
  }
  // Added after the fees: Faye's price, financed with the trip.
  if (pricing.insurance > 0) {
    rows.push([`Travel insurance${pricing.insurancePlan ? ` (${pricing.insurancePlan})` : ''}`, formatMoney(pricing.insurance, pricing.currency)]);
  }
  rows.push(['Trip total', formatMoney(pricing.grandTotalWithHold, pricing.currency), true]);
  return rows.map(([label, value, strong]) => `
    <div class="cost-row${strong ? ' cost-row-total' : ''}">
      <span class="cost-label">${esc(label)}</span>
      <span class="cost-value">${esc(value)}</span>
    </div>
  `).join('');
}

/**
 * Return the src attribute for the Defera logo. Prefers the inline data
 * URI (survives sandboxed popups); otherwise uses the caller-provided
 * absolute URL fallback; last resort is the same-origin path (which will
 * only work when the popup is same-origin).
 */
function renderLogo(brand, inlineLogoDataUri, fallbackLogoUrl) {
  const src = inlineLogoDataUri
    || fallbackLogoUrl
    || brand.logoUrl
    || '/defera-transparent.png';
  return `<img class="brand-logo" src="${esc(src)}" alt="${esc(brand.name)}" />`;
}

/**
 * `data` — output of buildTripDocumentData()
 * `options` — {
 *   inlineLogoDataUri?: string   — preferred logo source (base64 data URI)
 *   fallbackLogoUrl?: string     — absolute URL used if inlining failed
 * }
 */
export function renderTripDocumentHtml(data, options = {}) {
  const { document: docInfo, trip, customer, passengers, segments, pricing, payment } = data;
  const inlineLogoDataUri = options.inlineLogoDataUri || null;
  const fallbackLogoUrl = options.fallbackLogoUrl || null;

  const departureLine = trip.departureDate ? formatLongDate(trip.departureDate) : '';
  const bookingDateLine = trip.bookingDate ? formatLongDate(trip.bookingDate) : '';

  const paymentInfoBits = [];
  paymentInfoBits.push(`<span>Plan: <strong>${esc(payment.methodLabel)}</strong></span>`);
  if (payment.frequency) {
    const freq = payment.frequency === 'biweekly' ? 'Bi-weekly' : 'Weekly';
    paymentInfoBits.push(`<span>Frequency: <strong>${esc(freq)}</strong></span>`);
  }
  if (payment.depositPercentage != null) {
    paymentInfoBits.push(`<span>Deposit: <strong>${esc(payment.depositPercentage)}%</strong></span>`);
  }

  // Airline confirmation renders as a green-tinted chip in the banner
  // row alongside the booking-reference chip (only when the trip is
  // `completed` — this gate matches FlightDetailsModal's on-screen
  // disclosure rule). No trip-meta row, no dedicated confirmation-line.
  const confirmationChip = trip.confirmationDisclosed && trip.airlineConfirmation
    ? `
      <span class="ref-chip ref-chip-confirmation">
        <span class="label">Airline confirmation</span>
        <span class="value">${esc(trip.airlineConfirmation)}</span>
      </span>
    `
    : '';

  const disclosureLine = trip.confirmationDisclosed
    ? ''
    : `<p class="disclosure">Your airline confirmation number is released once your trip is fully paid and marked completed. Until then, this statement is your official record with Defera.</p>`;

  const passengerBlock = passengers.length
    ? passengers.map((p, i) => renderPassenger(p, i)).join('')
    : `<div class="pax-empty">No passengers on file.</div>`;

  const segmentBlock = renderSegments(segments);

  const title = `${esc(docInfo.title)} · ${esc(trip.reference || docInfo.number)}`;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <style>
    :root {
      --brand: ${BRAND_COLOR};
      --brand-soft: ${BRAND_SOFT};
      --muted: ${BRAND_TEXT_MUTED};
      --rule: ${BRAND_RULE};
      --sans: ${SANS_STACK};
    }
    /* Global type-scale — every size below is derived from a 9.5px base.
       Bumping/dropping this base scales the whole statement proportionally,
       which is how the room for 20-installment plans and 4-leg itineraries
       was added without redesigning individual sections. Roughly 88% of the
       original 10.5px baseline (round-1) and 90% of round-2 (10.5px). */
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #eef1f6;
      color: #101426;
      font-family: var(--sans);
      font-size: 9.5px;
      line-height: 1.35;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    /* ── Page shell ─────────────────────────────────────────────────── */
    .page {
      background: #ffffff;
      max-width: 8.3in;
      margin: 18px auto;
      padding: 0.42in 0.5in 0.32in;
      box-shadow: 0 6px 40px -20px rgba(15, 25, 82, 0.35);
      border: 1px solid #e6e9f2;
    }

    /* ── Header ─────────────────────────────────────────────────────── */
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      padding-bottom: 10px;
      border-bottom: 2.5px solid var(--brand);
      margin-bottom: 12px;
    }
    .brand-block { display: flex; align-items: center; gap: 12px; min-width: 0; }
    /* Print-optimized logo is already black-on-transparent (see
       public/defera-print-logo.png) so no CSS filter is needed. Filters
       are unreliable in some print pipelines (Safari's PDF exporter has
       historically dropped them). Sizing keeps the wordmark comfortably
       taller than body text without dominating the header. */
    .brand-logo {
      height: 34px;
      width: auto;
      max-width: 180px;
      object-fit: contain;
      display: block;
    }
    .brand-tagline {
      font-size: 8.5px;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--muted);
    }
    .doc-meta { text-align: right; font-size: 9px; color: var(--muted); }
    .doc-title {
      font-family: var(--sans);
      font-size: 16px;
      font-weight: 700;
      color: var(--brand);
      letter-spacing: 0.14em;
      margin: 0 0 3px;
      text-transform: uppercase;
      line-height: 1.1;
    }
    .doc-subtitle {
      font-size: 8.5px;
      color: #33405d;
      margin: 0;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-weight: 500;
    }
    .doc-number {
      margin-top: 4px;
      font-family: "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace;
      color: #33405d;
      font-size: 9px;
      letter-spacing: 0.02em;
    }
    .doc-issue-date { color: var(--muted); font-size: 8.5px; margin-top: 2px; }

    /* ── Trip banner + booking reference row ────────────────────────── */
    /* The route sits left-aligned in black text (no filled banner) so it
       reads as a natural document heading. Booking reference and airline
       confirmation (when disclosed — only on completed trips) both live
       as chips in this same section: the customer's two identifier chips
       share space with the route on wide layouts and break to a shared
       row below the route on narrow ones. */
    .trip-banner {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 16px;
      padding: 2px 0 8px;
      margin-bottom: 10px;
      border-bottom: 1px solid var(--rule);
      flex-wrap: wrap;
    }
    .trip-banner-left {
      display: flex;
      align-items: baseline;
      gap: 10px;
      flex-wrap: wrap;
      min-width: 0;
    }
    .trip-route {
      color: #101426;
      font-weight: 700;
      font-size: 18px;
      letter-spacing: -0.005em;
      line-height: 1.15;
    }
    /* Chip family: booking reference + airline confirmation share the same
       shell; only the color palette differs so the customer can distinguish
       them without reading the label. Booking ref = brand (royal blue),
       Confirmation = success green (only present on completed trips). */
    .ref-chip {
      display: inline-flex;
      align-items: baseline;
      gap: 5px;
      padding: 2px 8px;
      border-radius: 4px;
      background: var(--brand-soft);
      border: 1px solid #dbe0f0;
      font-size: 9.5px;
      color: #33405d;
      white-space: nowrap;
      line-height: 1.4;
    }
    .ref-chip .label {
      font-size: 7.5px;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      font-weight: 600;
      color: var(--muted);
    }
    .ref-chip .value {
      font-family: "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace;
      font-weight: 700;
      color: var(--brand);
      letter-spacing: 0.02em;
    }
    .ref-chip.ref-chip-confirmation {
      background: #eefaf1;
      border-color: #cae8d1;
    }
    .ref-chip.ref-chip-confirmation .label { color: #4b7d5e; }
    .ref-chip.ref-chip-confirmation .value { color: #135838; }
    .trip-status {
      font-size: 8.5px;
      text-transform: uppercase;
      letter-spacing: 0.16em;
      padding: 3px 10px;
      border-radius: 999px;
      background: var(--brand-soft);
      border: 1px solid #dbe0f0;
      color: var(--brand);
      font-weight: 700;
      white-space: nowrap;
    }
    .trip-status.trip-status-completed {
      background: #eefaf1;
      border-color: #cae8d1;
      color: #135838;
    }
    .trip-status.trip-status-cancelled,
    .trip-status.trip-status-defaulted {
      background: #fdecec;
      border-color: #f5c9c9;
      color: #a12727;
    }
    .trip-meta {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px 16px;
      padding: 2px 0 8px;
      margin-bottom: 10px;
      border-bottom: 1px solid var(--rule);
    }
    .trip-meta-cell .label {
      font-size: 7.5px;
      color: var(--muted);
      letter-spacing: 0.14em;
      text-transform: uppercase;
      margin: 0 0 2px;
      font-weight: 600;
    }
    .trip-meta-cell .value {
      font-size: 10.5px;
      color: #101426;
      font-weight: 600;
      line-height: 1.25;
    }

    /* ── Split panels (Customer + Cost Breakdown) ───────────────────── */
    .split-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: start;
      gap: 12px;
      margin-bottom: 10px;
    }
    .panel {
      border: 1px solid var(--rule);
      border-radius: 5px;
      padding: 9px 11px;
      background: #ffffff;
    }
    .panel-soft {
      background: var(--brand-soft);
      border: 1px solid #dbe0f0;
    }
    .panel-title {
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 0.16em;
      color: var(--brand);
      margin: 0 0 5px;
      font-weight: 700;
    }
    .kv-row {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      font-size: 10px;
      padding: 2px 0;
      align-items: baseline;
    }
    .kv-label { color: var(--muted); white-space: nowrap; }
    .kv-value { color: #101426; text-align: right; }

    /* ── Section headers ────────────────────────────────────────────── */
    .section { margin-bottom: 10px; }
    .section-title {
      font-size: 8.5px;
      text-transform: uppercase;
      letter-spacing: 0.16em;
      color: var(--brand);
      font-weight: 700;
      margin: 0 0 6px;
      padding-bottom: 3px;
      border-bottom: 1px solid var(--rule);
    }

    /* ── Segments ───────────────────────────────────────────────────── */
    .segment-group + .segment-group { margin-top: 7px; }
    .segment-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 3px;
      gap: 12px;
    }
    .segment-title {
      font-weight: 600;
      font-size: 10px;
      color: #101426;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .segment-date {
      color: var(--muted);
      font-size: 9.5px;
      white-space: nowrap;
    }
    /* Each seg-row is a card containing:
         1. .seg-primary — logo + depart time/code + → + arrive time/code
         2. .seg-meta    — carrier, flight #, aircraft, cabin
       The meta line lives BELOW the times inside the same card so long
       carrier / aircraft / cabin strings can breathe without truncating.
       Wrapping is enabled on .seg-meta for extreme cases (four-hop
       international itineraries with long-form aircraft names). */
    .seg-row {
      padding: 6px 10px;
      border: 1px solid var(--rule);
      border-radius: 4px;
      background: #fbfcfe;
    }
    .seg-row + .seg-row { margin-top: 3px; }
    .seg-primary {
      display: grid;
      grid-template-columns: 20px minmax(0, 1fr) 20px minmax(0, 1fr);
      gap: 10px;
      align-items: center;
    }
    .seg-logo {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .seg-logo img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
    }
    .seg-logo-placeholder {
      width: 18px;
      height: 18px;
      border-radius: 3px;
      background: #e6e9f2;
      display: block;
    }
    .seg-side { display: flex; align-items: baseline; gap: 6px; min-width: 0; white-space: nowrap; }
    .seg-side-right { justify-content: flex-end; }
    .seg-time { font-weight: 600; color: #101426; font-size: 11px; }
    .seg-code {
      font-weight: 700;
      color: var(--brand);
      letter-spacing: 0.04em;
      font-size: 10.5px;
    }
    .seg-arrow {
      text-align: center;
      color: #b6c0d6;
      font-size: 13px;
      line-height: 1;
    }
    .seg-meta {
      color: var(--muted);
      font-size: 9px;
      margin-top: 3px;
      padding-left: 30px; /* aligns with the depart time column above */
      /* Wrap onto a second line when the meta string is longer than the
         card width — losing "Main Plus" to an ellipsis is a common source
         of confusion; wrapping keeps every field visible. */
      white-space: normal;
      line-height: 1.4;
    }

    /* ── Passengers ─────────────────────────────────────────────────── */
    .pax-row {
      display: grid;
      grid-template-columns: 20px 1fr;
      gap: 10px;
      padding: 5px 10px;
      border: 1px solid var(--rule);
      border-radius: 4px;
      background: #fbfcfe;
      font-size: 9.5px;
    }
    .pax-row + .pax-row { margin-top: 3px; }
    .pax-number {
      text-align: center;
      color: var(--brand);
      font-weight: 700;
      font-size: 11px;
    }
    .pax-name {
      font-weight: 600;
      color: #101426;
      font-size: 10.5px;
    }
    .pax-name .pax-meta {
      font-weight: 400;
      color: var(--muted);
      font-size: 9px;
    }
    .pax-line { color: var(--muted); font-size: 9px; margin-top: 2px; }
    .pax-empty { color: var(--muted); font-size: 10px; padding: 5px 0; }

    /* ── Cost breakdown ─────────────────────────────────────────────── */
    .cost-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      font-size: 10px;
      padding: 3px 0;
      border-bottom: 1px dotted #e0e4ee;
    }
    .cost-row:last-child { border-bottom: none; }
    .cost-row-total {
      font-weight: 700;
      color: var(--brand);
      border-top: 1px solid var(--brand);
      margin-top: 4px;
      padding-top: 5px;
      font-size: 11px;
    }
    .cost-label { color: var(--muted); }
    .cost-row-total .cost-label { color: var(--brand); }
    .cost-value { font-variant-numeric: tabular-nums; color: #101426; }
    .cost-row-total .cost-value { color: var(--brand); }

    /* ── Payment plan strip + timeline table ────────────────────────── */
    .payment-strip {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: baseline;
      background: var(--brand-soft);
      border: 1px solid #dbe0f0;
      border-radius: 4px;
      padding: 6px 11px;
      margin-bottom: 6px;
      font-size: 9.5px;
      color: #33405d;
      flex-wrap: wrap;
    }
    .payment-strip strong { color: #101426; font-weight: 600; }
    .payment-strip .payment-info { display: flex; gap: 12px; flex-wrap: wrap; }
    .payment-strip .payment-progress { white-space: nowrap; }
    .pay-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9.5px;
    }
    .pay-table thead th {
      text-align: left;
      font-size: 7.5px;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      color: var(--muted);
      font-weight: 700;
      padding: 4px 8px;
      border-bottom: 1px solid var(--rule);
    }
    .pay-table th.pay-amount, .pay-table td.pay-amount {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .pay-row td {
      padding: 3px 8px;
      border-bottom: 1px solid #eef1f7;
      vertical-align: middle;
    }
    .pay-status { white-space: nowrap; }
    .pay-status .pay-dot {
      width: 6px;
      height: 6px;
      border-radius: 999px;
      display: inline-block;
      vertical-align: middle;
      margin-right: 6px;
    }
    .pay-dot-paid { background: #12854a; }
    .pay-dot-upcoming { background: #b6c0d6; }
    .pay-dot-pastdue { background: #c14545; }
    .pay-dot-skipped { background: #d0d5df; }
    .pay-hint {
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      font-weight: 600;
    }
    .pay-row-paid .pay-hint { color: #12854a; }
    .pay-row-pastdue .pay-hint { color: #c14545; }
    .pay-row-upcoming .pay-hint,
    .pay-row-skipped .pay-hint { color: var(--muted); }
    .pay-date { color: #101426; white-space: nowrap; }
    .pay-note { color: var(--muted); }
    .pay-total-row td {
      padding: 4px 8px;
      font-size: 10px;
      color: #33405d;
      border-top: 1px solid #eef1f7;
    }
    .pay-total-final td {
      color: var(--brand);
      font-weight: 700;
      font-size: 10.5px;
      border-top: 1px solid var(--brand);
    }
    .disclosure {
      font-size: 8.5px;
      color: var(--muted);
      margin: 6px 0 0;
      font-style: italic;
      line-height: 1.4;
    }
    .pay-empty {
      color: var(--muted);
      font-size: 10px;
      padding: 10px 0;
      text-align: center;
    }

    /* ── Footer ─────────────────────────────────────────────────────── */
    .footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 20px;
      padding-top: 8px;
      margin-top: 10px;
      border-top: 1px solid var(--rule);
      font-size: 8.5px;
      color: var(--muted);
      line-height: 1.5;
    }
    .footer .brand-mark {
      color: var(--brand);
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .footer .terms {
      max-width: 62%;
      line-height: 1.45;
    }
    .footer .contact {
      text-align: right;
      white-space: nowrap;
    }
    .footer .contact strong { color: #33405d; }

    /* ── Print rules ────────────────────────────────────────────────── */
    /* Chrome, Edge, and Safari overlay a default header/footer (URL, page
       title, date, page number) inside the @page margin area on every
       printed page. On our statement that shows up as e.g. "Trip Statement
       · GJ0NSR  7/6/26, 4:26 PM" pinned to the very top of the paper —
       looks like our own content has fallen off the page. Setting
       @page margin to 0 collapses that reserved area and stops the
       browser from painting into it; the visible page margin is then
       delivered by .page's own padding instead. */
    @page { size: letter; margin: 0; }
    @media print {
      html, body { background: #ffffff; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page {
        margin: 0;
        padding: 0.42in 0.5in;
        border: none;
        box-shadow: none;
        max-width: none;
      }
      .segment-group, .pax-row, .cost-row, .pay-row { break-inside: avoid; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <header class="header">
      <div class="brand-block">
        ${renderLogo(docInfo.brand, inlineLogoDataUri, fallbackLogoUrl)}
        <span class="brand-tagline">${esc(docInfo.brand.tagline)}</span>
      </div>
      <div class="doc-meta">
        <h1 class="doc-title">${esc(docInfo.title)}</h1>
        <p class="doc-subtitle">Booking summary &amp; payment schedule</p>
        <div class="doc-number">${esc(docInfo.number)}</div>
        <div class="doc-issue-date">Issued ${esc(formatLongDate(docInfo.issueDate.toISOString()))}</div>
      </div>
    </header>

    <div class="trip-banner">
      <div class="trip-banner-left">
        <span class="trip-route">${esc(trip.route || trip.routeShort || 'Trip')}</span>
        ${trip.reference ? `
          <span class="ref-chip">
            <span class="label">Booking reference</span>
            <span class="value">${esc(trip.reference)}</span>
          </span>
        ` : ''}
        ${confirmationChip}
      </div>
      <span class="trip-status trip-status-${esc(trip.status || 'pending')}">${esc(trip.statusLabel)}</span>
    </div>

    <div class="trip-meta">
      <div class="trip-meta-cell">
        <p class="label">Trip type</p>
        <div class="value">${esc(trip.type)}${trip.cabinClass ? ` · ${esc(trip.cabinClass)}` : ''}</div>
      </div>
      <div class="trip-meta-cell">
        <p class="label">Departure</p>
        <div class="value">${esc(departureLine || '—')}</div>
      </div>
      <div class="trip-meta-cell">
        <p class="label">Passengers</p>
        <div class="value">${esc(trip.passengerCount || passengers.length)}</div>
      </div>
      <div class="trip-meta-cell">
        <p class="label">Booked on</p>
        <div class="value">${esc(bookingDateLine || '—')}</div>
      </div>
    </div>

    <div class="split-2">
      <div class="panel">
        <p class="panel-title">Customer</p>
        ${renderKV('Name', customer.name)}
        ${renderKV('Email', customer.email)}
        ${renderKV('Phone', customer.phone)}
      </div>
      <div class="panel panel-soft">
        <p class="panel-title">Cost breakdown</p>
        ${renderCostBreakdown(pricing)}
      </div>
    </div>

    ${segments.length ? `
      <section class="section">
        <h3 class="section-title">Flight itinerary</h3>
        ${segmentBlock}
      </section>
    ` : ''}

    <section class="section">
      <h3 class="section-title">Passengers</h3>
      ${passengerBlock}
    </section>

    <section class="section">
      <h3 class="section-title">Payment plan &amp; history</h3>
      <div class="payment-strip">
        <div class="payment-info">${paymentInfoBits.join('')}</div>
        <div class="payment-progress"><strong>${esc(formatMoneyCents(payment.totalPaidCents, payment.currency))}</strong> of <strong>${esc(formatMoneyCents(payment.totalCents, payment.currency))}</strong> paid</div>
      </div>
      ${renderPaymentTimeline(payment)}
      ${disclosureLine}
    </section>

    <footer class="footer">
      <div class="terms">
        <div class="brand-mark">${esc(docInfo.brand.name)}</div>
        This statement is generated by Defera for your records. Amounts reflect the trip's stored payment schedule and history at the time of download. For questions or reconciliation, reach us using the contact information on the right — please reference your booking reference ${esc(trip.reference || docInfo.number)}.
      </div>
      <div class="contact">
        <strong>${esc(docInfo.brand.website)}</strong><br />
        ${esc(docInfo.brand.contactPage)}<br />
        ${esc(docInfo.brand.supportPhone)}
      </div>
    </footer>
  </div>
</body>
</html>`;
}
